import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { logout } from './authSlice';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';

const defaultMeta = {
  page: 1,
  page_size: 30,
  total_items: 0,
  total_pages: 0
};

const padNumber = (value) => String(value).padStart(2, '0');

const formatDateForInput = (date) =>
  `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())}`;

const formatDateTimeLocal = (date) =>
  `${formatDateForInput(date)}T${padNumber(date.getHours())}:${padNumber(date.getMinutes())}`;

const toApiDateTime = (value, fallbackSeconds = '00') => {
  if (!value) return null;

  const [datePart = '', timePart = '00:00'] = String(value).split('T');
  const [hours = '00', minutes = '00', seconds = fallbackSeconds] = timePart.split(':');
  return `${datePart} ${hours}:${minutes}:${seconds}`;
};

const createDefaultDateTimeRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 0, 0);

  return {
    start: formatDateTimeLocal(start),
    end: formatDateTimeLocal(end)
  };
};

const defaultRange = createDefaultDateTimeRange();

const defaultQuery = {
  page: 1,
  pageSize: 30,
  startDateTime: defaultRange.start,
  endDateTime: defaultRange.end,
  sortBy: 'date',
  sortDirection: 'desc'
};

const buildMockDailyReportRows = () => {
  const today = new Date();
  const rows = [];

  for (let index = 0; index < 120; index += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - index);
    date.setHours(12, 0, 0, 0);

    const dayFactor = index % 7;
    const totalTransactions = 220 + (120 - index) * 3 + dayFactor * 17;
    const totalTickets = totalTransactions * 2 + (index % 5) * 9;
    const totalFare = totalTickets * 3500 + (index % 4) * 1250;

    rows.push({
      id: date.toISOString(),
      date: date.toISOString().slice(0, 10),
      totalTransactions,
      totalTickets,
      totalFare
    });
  }

  return rows;
};

const mockDailyReportRows = buildMockDailyReportRows();

const initialState = {
  items: [],
  meta: defaultMeta,
  loading: false,
  error: null,
  query: defaultQuery,
  exportStatus: 'idle',
  exportError: null,
  isMockData: false
};

const buildAuthHeaders = (getState, extra = {}) => {
  const { auth } = getState();
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...extra
  };

  if (auth?.accessToken) {
    headers.Authorization = `Bearer ${auth.accessToken}`;
  }

  return headers;
};

const handleUnauthorized = (dispatch) => {
  dispatch(logout());
  if (typeof window !== 'undefined') {
    window.location.replace('/login');
  }
};

const handleApiResponse = async (response, dispatch) => {
  if (response.status === 401) {
    handleUnauthorized(dispatch);
    throw new Error('Unauthorized');
  }

  const contentType = response.headers.get('content-type');
  let data;
  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    const message =
      (data &&
        typeof data === 'object' &&
        (data.resultDesc || data.resultMessage || data.message)) ||
      response.statusText ||
      'Request failed';
    throw new Error(message);
  }

  if (data && typeof data === 'object' && Number(data.resultCode) === 401) {
    handleUnauthorized(dispatch);
    throw new Error(data.resultDesc || data.resultMessage || data.message || 'Unauthorized');
  }

  return data;
};

const normalizeQuery = (params = {}) => ({
  page: params.page ?? defaultQuery.page,
  pageSize: params.pageSize ?? defaultQuery.pageSize,
  startDateTime: params.startDateTime ?? defaultQuery.startDateTime,
  endDateTime: params.endDateTime ?? defaultQuery.endDateTime,
  sortBy: params.sortBy ?? defaultQuery.sortBy,
  sortDirection: params.sortDirection ?? defaultQuery.sortDirection
});

const buildQueryString = (params = {}) => {
  const normalized = normalizeQuery(params);
  const search = new URLSearchParams();

  if (normalized.page != null) search.set('page', normalized.page);
  if (normalized.pageSize != null) search.set('page_size', normalized.pageSize);
  if (normalized.startDateTime) {
    search.set('start_datetime', toApiDateTime(normalized.startDateTime, '00'));
  }
  if (normalized.endDateTime) {
    search.set('end_datetime', toApiDateTime(normalized.endDateTime, '59'));
  }
  if (normalized.sortBy) search.set('sort_by', normalized.sortBy);
  if (normalized.sortDirection) search.set('sort_direction', normalized.sortDirection);

  const queryString = search.toString();
  return queryString ? `?${queryString}` : '';
};

const normalizeItem = (item) => ({
  id: item.id || item.date,
  date: item.date,
  totalTransactions: Number(item.total_transactions ?? item.totalTransactions ?? 0),
  totalTickets: Number(item.total_tickets ?? item.totalTickets ?? 0),
  totalFare: Number(item.total_fare ?? item.totalFare ?? 0)
});

const sortItems = (items, sortBy, sortDirection) => {
  const direction = sortDirection === 'asc' ? 1 : -1;
  return [...items].sort((left, right) => {
    const a = left[sortBy];
    const b = right[sortBy];

    if (a < b) return -1 * direction;
    if (a > b) return 1 * direction;
    return 0;
  });
};

const getMockPayload = (params = {}) => {
  const query = normalizeQuery(params);
  const start = query.startDateTime ? new Date(query.startDateTime) : null;
  const end = query.endDateTime ? new Date(query.endDateTime) : null;

  const filtered = mockDailyReportRows.filter((item) => {
    const rowDate = new Date(`${item.date}T12:00:00`);
    if (start && rowDate < start) return false;
    if (end && rowDate > end) return false;
    return true;
  });

  const sorted = sortItems(filtered, query.sortBy, query.sortDirection);
  const startIndex = (query.page - 1) * query.pageSize;
  const items = sorted.slice(startIndex, startIndex + query.pageSize);
  const totalItems = sorted.length;

  return {
    items,
    meta: {
      page: query.page,
      page_size: query.pageSize,
      total_items: totalItems,
      total_pages: Math.ceil(totalItems / query.pageSize)
    },
    query,
    isMockData: true
  };
};

export const fetchDailyReports = createAsyncThunk(
  'dailyReports/fetchDailyReports',
  async (params, { getState, dispatch, rejectWithValue }) => {
    const normalized = normalizeQuery(params);

    try {
      const queryString = buildQueryString(normalized);
      const response = await fetch(`${API_BASE_URL}/api/daily-report${queryString}`, {
        method: 'GET',
        headers: buildAuthHeaders(getState)
      });

      const data = await handleApiResponse(response, dispatch);
      const items = Array.isArray(data?.data) ? data.data.map(normalizeItem) : [];
      const rawMeta = data?.meta || defaultMeta;

      return {
        items,
        meta: {
          page: Number(rawMeta.page ?? defaultMeta.page),
          page_size: Number(rawMeta.page_size ?? defaultMeta.page_size),
          total_items: Number(rawMeta.total_items ?? defaultMeta.total_items),
          total_pages: Number(rawMeta.total_pages ?? defaultMeta.total_pages)
        },
        query: normalized,
        isMockData: false
      };
    } catch (error) {
      if (error instanceof TypeError || /Failed to fetch|NetworkError|404|Not Found/i.test(error.message)) {
        return getMockPayload(normalized);
      }

      return rejectWithValue(error.message || 'Failed to load daily report.');
    }
  }
);

export const exportDailyReports = createAsyncThunk(
  'dailyReports/exportDailyReports',
  async (params, { getState, dispatch, rejectWithValue }) => {
    const normalized = normalizeQuery(params);

    try {
      const search = new URLSearchParams();
      if (normalized.startDateTime) {
        search.set('start_datetime', toApiDateTime(normalized.startDateTime, '00'));
      }
      if (normalized.endDateTime) {
        search.set('end_datetime', toApiDateTime(normalized.endDateTime, '59'));
      }
      if (normalized.sortBy) search.set('sort_by', normalized.sortBy);
      if (normalized.sortDirection) search.set('sort_direction', normalized.sortDirection);
      search.set('all', 'true');

      const response = await fetch(
        `${API_BASE_URL}/api/daily-report/export?${search.toString()}`,
        {
          method: 'GET',
          headers: buildAuthHeaders(getState, {
            Accept:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/json'
          })
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(dispatch);
        }
        const message = await response.text();
        throw new Error(message || response.statusText || 'Failed to export daily report.');
      }

      const blob = await response.blob();
      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const disposition = response.headers.get('content-disposition') || '';
        const match = disposition.match(/filename="?(.+?)"?$/i);
        const filename = match?.[1] || 'daily-report.xlsx';

        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
      }

      return { isMockData: false };
    } catch (error) {
      try {
        const mockPayload = getMockPayload({ ...normalized, page: 1, pageSize: 1000 });
        const header = ['Date', 'Total Transaction', 'Total Ticket', 'Total Fare'];
        const csvRows = [
          header.join(','),
          ...mockPayload.items.map((row) =>
            [row.date, row.totalTransactions, row.totalTickets, row.totalFare].join(',')
          )
        ];

        const blob = new Blob([csvRows.join('\n')], {
          type: 'application/vnd.ms-excel;charset=utf-8;'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'daily-report.xls';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return { isMockData: true };
      } catch (fallbackError) {
        return rejectWithValue(
          error.message || fallbackError.message || 'Failed to export daily report.'
        );
      }
    }
  }
);

const dailyReportsSlice = createSlice({
  name: 'dailyReports',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDailyReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDailyReports.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.items;
        state.meta = action.payload.meta;
        state.query = action.payload.query;
        state.isMockData = action.payload.isMockData;
      })
      .addCase(fetchDailyReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load daily report.';
      })
      .addCase(exportDailyReports.pending, (state) => {
        state.exportStatus = 'loading';
        state.exportError = null;
      })
      .addCase(exportDailyReports.fulfilled, (state) => {
        state.exportStatus = 'succeeded';
        state.exportError = null;
      })
      .addCase(exportDailyReports.rejected, (state, action) => {
        state.exportStatus = 'failed';
        state.exportError = action.payload || 'Failed to export daily report.';
      });
  }
});

export default dailyReportsSlice.reducer;
